import {
    useCallback,
    useEffect,
} from 'react'
import noop from 'lodash/noop'
import {
    ArchiveStatus,
} from 'app/enums/common.enums'
import ChipSelect from 'app/shared-components/ChipSelect'

import {
    Validate,
} from 'app/types/form.types'
import {
    Contact,
} from 'app/Apps/ContactManagement/Contacts/Contacts.types'

import useValidateItem from '../../hooks/useValidateItem'

import {
    valueConverterToArray,
} from './utils/converters'
import getValueLabelByEmail from './utils/getValueLabelByEmail'
import useCommonMultiContactSelectorLogic from './hooks/useCommonMultiContactSelectorLogic'

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

export default function MultipleContactSelector({
    name = '',
    value = EMPTY_ARRAY,
    onChange,
    title = 'Contacts',
    disabled = false,
    required = false,
    validate = noop,
    setErrors = noop,
    includeFilters = EMPTY_OBJECT,
    archivedStatus = ArchiveStatus.ALL,
}: Readonly<{
    value?: number[],
    name?: string,
    onChange: (newValue: number[]) => void,
    title?: string,
    disabled?: boolean,
    required?: boolean,
    validate?: Validate<Contact>
    setErrors?: (e: [string, any[]]) => void,
    includeFilters?: Record<string, any>,
    archivedStatus?: ArchiveStatus,
}>) {
    const {
        contacts, setContacts, loadOptions, contactsByIds,
    } = useCommonMultiContactSelectorLogic({
        valueConverter: (listId: number[]) => {
            return listId
        },
        listIds: value,
        includeFilters,
        archivedStatus,
    })

    useEffect(() => {
        if (
            contactsByIds
            && JSON.stringify(contactsByIds) !== JSON.stringify(contacts)
        ) {
            setContacts(contactsByIds)
        }
    }, [
        contacts,
        contactsByIds,
        setContacts,
    ])

    const onChangeValue = useCallback(
        (newValue: { id: number }[], actionParams): void => {
            let updatedValues

            if (actionParams?.action === 'remove-value') {
                updatedValues = contacts.filter(({
                    id,
                }) => {
                    return id !== actionParams?.removedValue?.id
                })
            } else {
                updatedValues = newValue
            }

            onChange(updatedValues ? valueConverterToArray(updatedValues) : [])
        },
        [
            contacts,
            onChange,
        ],
    )

    const {
        errors, validated,
    } = useValidateItem({
        validate,
        value,
        setFormError: setErrors,
        key: name,
    })

    return (
        <ChipSelect
            title={title}
            value={contacts}
            onChange={onChangeValue}
            loadOptions={loadOptions}
            getValueLabel={getValueLabelByEmail}
            showTitleOptions
            disabled={disabled}
            required={required}
            errors={errors}
            validated={validated}
            menuShouldBlockScroll
            menuPortalTarget={document.body}
        />
    )
}
